// deps
import { useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { PaymentGroupDataDef } from "@swiggy-private/payment-ui";
import { AnalyticsContext, CheckoutRoute, useNavigate } from "@swiggy-private/checkout-core";
import { ApiFetchState, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// analytics
import { paymentBannerClickEvent } from "@payment-client/analytics/events/Payment";
import { impressionPayAfterOrder } from "@payment-client/analytics/events/PayOnDelivery";

// helpers
import {
    isAddNewCardPromoBanner,
    isLazypayPromoBanner,
    isPpmFoodcardPromoBanner,
    isRegularFoodcardPromoBanner,
    isPaymentMethodDowntimeBanner,
    isInAppUPIBanner,
    isEmiBanner,
} from "@payment-client/helpers/PaymentBannerHelper";
import { getFoodcardPaymentGroup, getNewcardPaymentMethod } from "@payment-client/helpers/CardHelper";
import { findPayLaterPaymentMethod, getPayLaterPaymentGroup } from "@payment-client/helpers/PaylaterHelper";
import { getPreferredGroup } from "@payment-client/helpers/PreferredHelper";
import { getCODPaymentMethodForAutoPay } from "@payment-client/helpers/CodHelper";
import { getNewInAppUpiPaymentMethod } from "@payment-client/helpers/InAppUpiHelper";

// contexts
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// constants
import { PAYMENT_FALLBACK_TYPES } from "@payment-client/constants/PaymentFallback";
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-client/constants/Order";
import { INAPP_UPI_STATE } from "@payment-client/constants/InAppUpi";

// hooks
import { useOnPay } from "./UseOnPay";

export interface CustomBannerActions {
    paymentActions: Pick<PaymentGroupHandlerProps, "selectedPaymentId" | "onSelect" | "onPay">;
    onMorePaymentsOptionSelect: (paymentGroup: PaymentGroupDataDef, frictionGroups?: PaymentGroupDataDef[]) => void;
}

export interface UsePaymentBannerReturnType {
    handleCustomActionOnBanner: (id: string, actions: CustomBannerActions) => void;
    paymentMethodDowntimeBannerData: {
        showPaymentMethodDowntimeHalfCard: boolean;
        closeHalfCard: VoidFunction;
        onHalfCardCtaClick: VoidFunction;
        downtimeMethodDisplayName: string;
        onHalfCardLoad: VoidFunction;
    };
}

export interface UsePaymentBannerParams {
    banner?: GetPaymentMethodsResponse["banner"];
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
}

export const usePaymentBanner = ({ banner, paymentGroups }: UsePaymentBannerParams): UsePaymentBannerReturnType => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const [showPaymentMethodDowntimeHalfCard, setPaymentMethodDowntimeHalfCard] = useState(false);
    const onPay = useOnPay();
    const { paymentResponse } = useContext(ShowPaymentContext);

    // Paylater Specific Data - for banners
    const { paylaterState } = useContext(PaylaterLinkedContext);
    const paylaterMethod = useMemo(
        () => findPayLaterPaymentMethod(paymentGroups, PAYMENT_FALLBACK_TYPES.LAZYPAY),
        [paymentGroups],
    );
    const paylaterGroup = useMemo(() => getPayLaterPaymentGroup(paymentGroups), [paymentGroups]);

    // For Payment method downtime banner
    const codPaymentMethod = useMemo(() => {
        return getCODPaymentMethodForAutoPay(paymentResponse?.paymentGroup);
    }, [paymentResponse?.paymentGroup]);
    const downtimeMethodDisplayName = useMemo(
        () => paymentResponse?.paymentMethodDowntime?.paymentPageHalfcardPlaceholders.paymentMethodDisplayName ?? "",
        [paymentResponse?.paymentMethodDowntime?.paymentPageHalfcardPlaceholders.paymentMethodDisplayName],
    );

    const navigateTo = useNavigate();

    const handleCustomActionOnBanner = useCallback(
        (id: string, actions: CustomBannerActions) => {
            paymentBannerClickEvent(id, clickEvent);

            const { onSelect } = actions.paymentActions;

            if (isAddNewCardPromoBanner(banner)) {
                const newCardMethod = getNewcardPaymentMethod(paymentGroups);
                if (newCardMethod.isSome()) {
                    onSelect(newCardMethod.unwrap());
                }
                return;
            }

            if (isLazypayPromoBanner(banner)) {
                if (paylaterMethod.isSome()) {
                    /*
                     * Non-linked user: redirection to link confirmation page
                     * Linked user, for whom the method is present in friction layout: redirection to friction layout
                     * Linked user, for whom the method is present is not in friction layout: place order directly
                     */
                    const method = paylaterMethod.unwrap();
                    const { linked, fetchState } = paylaterState[method.name];
                    if (fetchState !== ApiFetchState.SUCCESS) return;
                    if (!linked) {
                        navigateTo(CheckoutRoute.PAYLATER_LINKING_CONFIRMATION);
                        return;
                    }
                    if (paylaterGroup.isNone()) return;
                    const group = paylaterGroup.unwrap();
                    if (group.isFrictionHiddenGroup) {
                        actions.onMorePaymentsOptionSelect(group);
                    } else if (method.enabled) {
                        actions.paymentActions.onPay(method);
                    }
                }
                return;
            }

            if (isPpmFoodcardPromoBanner(banner)) {
                const preferredGroup = getPreferredGroup(paymentGroups);
                // No Preferred Method available
                if (preferredGroup.isNone()) return;

                const group = preferredGroup.unwrap();
                const preferredFoodCards = group.paymentMethods.filter((method) => {
                    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.FOOD_CARD;
                });

                // No preferred food cards available
                if (preferredFoodCards.length === 0) return;

                // Place order using top-most food-card present in preferred group
                const foodCardMethod = preferredFoodCards[0];
                if (foodCardMethod.enabled) {
                    actions.paymentActions.onPay(foodCardMethod);
                }
            }

            if (isRegularFoodcardPromoBanner(banner)) {
                const foodCardGroup = getFoodcardPaymentGroup(paymentGroups);
                const group = foodCardGroup.unwrap();
                if (group.isFrictionHiddenGroup) {
                    actions.onMorePaymentsOptionSelect(group);
                }
            }

            if (isInAppUPIBanner(banner)) {
                const newInAppUpiMethod = getNewInAppUpiPaymentMethod(paymentGroups, INAPP_UPI_STATE.ONBOARDING);
                if (newInAppUpiMethod.isSome()) {
                    onSelect(newInAppUpiMethod.unwrap());
                }
                return;
            }

            if (isPaymentMethodDowntimeBanner(banner)) {
                setPaymentMethodDowntimeHalfCard(true);
            }

            if (isEmiBanner(banner)) {
                navigateTo(CheckoutRoute.EMI_SELECTION);
            }
        },
        [
            clickEvent,
            banner,
            paymentGroups,
            paylaterMethod,
            paylaterState,
            paylaterGroup,
            navigateTo,
        ],
    );

    const closeHalfCard = useCallback(() => {
        setPaymentMethodDowntimeHalfCard(false);
    }, []);

    const onHalfCardCtaClick = useCallback(() => {
        closeHalfCard();
        onPay(
            codPaymentMethod,
            {
                [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: 0,
            },
            {
                isPaymentMethodFacingDowntime: true,
            },
        );
    }, [closeHalfCard, codPaymentMethod, onPay]);

    const onHalfCardLoad = useCallback(() => {
        impressionPayAfterOrder(impressionEvent);
    }, [impressionEvent]);

    return {
        handleCustomActionOnBanner,
        paymentMethodDowntimeBannerData: {
            showPaymentMethodDowntimeHalfCard,
            closeHalfCard,
            onHalfCardCtaClick,
            onHalfCardLoad,
            downtimeMethodDisplayName,
        },
    };
};
